import { add, format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { CalendarIcon, LeftAngleIcon, RightAngleIcon } from '../../../../../assets/svg';
import { Select } from '../../../../../components/form/select';
import { Input } from '../../../../../components/input';
import { useClickOutside } from '../../../../../hooks/useClickOutside';
import { PaymentMultiuse } from '../../../../../models/payment-links-multiuse';
import {
	FindHour,
	getMonthAndYearFormat,
	getNextHalfHour,
	HoursMinutes,
	transformDateToState,
} from '../../../../../utils/dates';
import { Values } from '../index';
import {
	ChangeMonthContainer,
	ContainerFilterTime,
	CurrentMonthYear,
	HeaderMonthYearSelected,
	InputContainer,
	PLCardTitle,
	ShowDatePickerContainer,
} from '../styles';

interface TypeExpirationProps {
	state: Values;
	setState: React.Dispatch<React.SetStateAction<Values>>;
	selectedToEdit?: PaymentMultiuse;
	handleExpirationTypeChange: (expirationType: 'date' | 'num_payments') => void;
	handleChangeInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const TypeExpiration = ({
	state,
	setState,
	selectedToEdit,
	handleExpirationTypeChange,
	handleChangeInput,
}: TypeExpirationProps) => {
	//State to handle the expiration type and show the date picker or the input for the number of payments
	//If the user is editing a payment link, the showConfigExpiration will be false
	//State to handle the date picker render
	const fromElementRef = useRef<HTMLDivElement>(null);
	const toElementRef = useRef<HTMLDivElement>(null);
	const [showCalendar, setShowCalendar] = useState({
		from: false,
		to: false,
	});
	//The idea of this array is to restrict the hours and minutes to end hour and minutes, to just select the next half hour that is available
	const [hoursMinuteTo, setHoursMinuteTo] = useState(HoursMinutes);

	const handleShowCalendar = (property: 'from' | 'to') => {
		if (property === 'from') {
			setShowCalendar({ ...showCalendar, from: !showCalendar.from, to: false });
		} else {
			setShowCalendar({ ...showCalendar, to: !showCalendar.to, from: false });
		}
	};

	const handleConfigExpiration = () => {
		if (!selectedToEdit) {
			if (state.showConfigExpiration) {
				setState({
					...state,
					initExpirationDate: transformDateToState(new Date()),
					endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
					initExpirationTime: getNextHalfHour(format(new Date(), 'HH:mm')),
					endExpirationTime: getNextHalfHour(format(add(new Date(), { days: 1 }), 'HH:mm')),
					showConfigExpiration: false,
				});
			} else {
				setState({
					...state,
					initExpirationDate: transformDateToState(new Date()),
					endExpirationDate: transformDateToState(add(new Date(), { days: 1 })),
					initExpirationTime: format(new Date(), 'HH:mm'),
					endExpirationTime: format(add(new Date(), { days: 1 }), 'HH:mm'),
					showConfigExpiration: true,
				});
			}
		}
	};

	useClickOutside({
		elementRef: fromElementRef,
		callback: () => setShowCalendar({ ...showCalendar, from: false }),
	});

	useClickOutside({
		elementRef: toElementRef,
		callback: () => setShowCalendar({ ...showCalendar, to: false }),
	});

	useEffect(() => {
		setState({
			...state,
			showConfigExpiration: selectedToEdit ? false : state.showConfigExpiration,
		});
	}, []);

	useEffect(() => {
		//This useEffect is to set the next half hour available to select in the end expiration time when the init expiration time is selected
		//Check if the init expiration date and end expiration date are the same, if they are, set the next half hour available to select in the end expiration time
		if (state.initExpirationDate === state.endExpirationDate) {
			const index = HoursMinutes.findIndex((item) => item.value === state.initExpirationTime);
			const newHoursMinuteTo = HoursMinutes.slice(index + 1);
			setHoursMinuteTo(newHoursMinuteTo);
			setState({ ...state, endExpirationTime: newHoursMinuteTo[0].value });
		} else {
			setHoursMinuteTo(HoursMinutes);
		}
	}, [state.initExpirationTime, state.initExpirationDate, state.endExpirationDate]);

	const onChangeDate = (date: Date, property: 'from' | 'to') => {
		if (property === 'from') {
			setState({
				...state,
				initExpirationDate: transformDateToState(date),
				endExpirationDate: transformDateToState(add(date, { days: 1 })),
			});
			setShowCalendar({ ...showCalendar, from: false });
		} else {
			setState({ ...state, endExpirationDate: transformDateToState(date) });
			setShowCalendar({ ...showCalendar, to: false });
		}
	};

	const transformDateToPicker = (date?: string) => {
		if (!date) return;
		const [day, month, year] = date.split('/');
		return new Date(Number(year), Number(month) - 1, Number(day));
	};

	const handleChangeDate = (date: string, property: 'from' | 'to') => {
		if (property === 'from') {
			setState({ ...state, initExpirationTime: date });
		} else {
			setState({ ...state, endExpirationTime: date });
		}
	};

	const getTypeOptions = (expirationType: string) => {
		if (expirationType === 'date' || expirationType === 'DATE') {
			return 0;
		} else {
			return 1;
		}
	};

	return (
		<>
			<Select
				options={[
					{
						value: 'date',
						label: 'Por fecha',
						show: false,
					},
					{
						value: 'num_payments',
						label: 'Por número de pagos exitosos',
						show: false,
					},
				]}
				size="large"
				sizeWidth="max"
				onChange={(e) => {
					handleExpirationTypeChange(e.value as 'date' | 'num_payments');
				}}
				defaultOption={
					selectedToEdit
						? getTypeOptions(selectedToEdit.expirationtype)
						: getTypeOptions(state.expirationType)
				}
				titleLabel="Tipo de expiración"
				disabled={selectedToEdit ? true : state.selectButtonDisabled}
			/>

			{state.showConfigExpiration && state.expirationType === 'date' && !selectedToEdit && (
				<PLCardTitle selected={false} onStart>
					El enlace expirará por defecto a las 24 horas.{' '}
					<span onClick={handleConfigExpiration}>Configurar</span>
				</PLCardTitle>
			)}
			{!state.showConfigExpiration && state.expirationType === 'date' && !selectedToEdit && (
				<PLCardTitle selected={false} onStart>
					Aplicar fecha{' '}
					<span onClick={handleConfigExpiration}>expiración por defecto a las 24 horas</span>
				</PLCardTitle>
			)}

			{/* Aplicar los inputs de las fechas en esta seccion */}
			{!state.showConfigExpiration && state.expirationType === 'date' && !selectedToEdit && (
				<ContainerFilterTime>
					<InputContainer>
						<Input
							label="Fecha de inicio"
							name="initExpirationDate"
							value={state.initExpirationDate}
							onChange={() => {
								handleShowCalendar('from');
							}}
							onClick={() => {
								handleShowCalendar('from');
							}}
							disabled={selectedToEdit ? true : false}
							variant="large"
							rightIcon={<CalendarIcon />}
							rightIconClick={() => handleShowCalendar('from')}
							fontSize="16px"
						/>
					</InputContainer>

					<Select
						options={HoursMinutes}
						size="large"
						sizeWidth="max"
						titleLabel="Hora de inicio"
						sizeHeight="150px"
						onChange={(e) => {
							handleChangeDate(e.value as string, 'from');
						}}
						disabled={selectedToEdit ? true : false}
						defaultOption={FindHour(state.initExpirationTime)}
					/>

					{/* Fecha de vencimiento */}
					<InputContainer>
						<Input
							label="Fecha de vencimiento"
							name="endExpirationDate"
							value={state.endExpirationDate}
							onChange={() => {
								handleShowCalendar('to');
							}}
							onClick={() => {
								handleShowCalendar('to');
							}}
							disabled={selectedToEdit ? true : false}
							variant="large"
							rightIcon={<CalendarIcon />}
							rightIconClick={() => handleShowCalendar('to')}
							fontSize="16px"
						/>
					</InputContainer>
					<Select
						key={state.endExpirationTime}
						options={hoursMinuteTo}
						size="large"
						sizeWidth="max"
						titleLabel="Hora de vencimiento"
						sizeHeight="100px"
						onChange={(e) => {
							handleChangeDate(e.value as string, 'to');
						}}
						disabled={selectedToEdit ? true : false}
						defaultOption={FindHour(state.endExpirationTime, hoursMinuteTo)}
					/>
				</ContainerFilterTime>
			)}

			{selectedToEdit && selectedToEdit.expirationtype === 'DATE' && (
				<ContainerFilterTime>
					<InputContainer>
						<Input
							label="Fecha de inicio"
							name="initExpirationDate"
							value={selectedToEdit.initexpiredatdate}
							onChange={() => {
								handleShowCalendar('from');
							}}
							onClick={() => {
								handleShowCalendar('from');
							}}
							disabled
							variant="large"
							rightIcon={<CalendarIcon />}
							rightIconClick={() => handleShowCalendar('from')}
							fontSize="16px"
						/>
					</InputContainer>
					<Input
						label="Hora de inicio"
						variant="large"
						disabled
						value={selectedToEdit.initexpiredattime}
						fontSize="16px"
					/>

					{/* Fecha de vencimiento */}
					<InputContainer>
						<Input
							label="Fecha de vencimiento"
							name="endExpirationDate"
							value={selectedToEdit.endexpiredatdate}
							onChange={() => {
								handleShowCalendar('to');
							}}
							onClick={() => {
								handleShowCalendar('to');
							}}
							disabled
							variant="large"
							rightIcon={<CalendarIcon />}
							rightIconClick={() => handleShowCalendar('to')}
							fontSize="16px"
						/>
					</InputContainer>
					<Input
						label="Hora de vencimiento"
						variant="large"
						disabled
						value={selectedToEdit.endexpiredattime}
						fontSize="16px"
					/>
				</ContainerFilterTime>
			)}

			{showCalendar.from && (
				<ShowDatePickerContainer y="top" x="left">
					<ReactDatePicker
						inline
						selected={
							state.initExpirationDate
								? transformDateToPicker(state.initExpirationDate)
								: new Date()
						}
						minDate={new Date()}
						locale={'es'}
						onChange={(date) => onChangeDate(date as Date, 'from')}
						renderCustomHeader={(params) => (
							<>
								<HeaderMonthYearSelected>
									<ChangeMonthContainer
										onClick={() => {
											if (!params.prevMonthButtonDisabled) params.decreaseMonth();
										}}
										allowClick={!params.prevMonthButtonDisabled}
									>
										<LeftAngleIcon />
									</ChangeMonthContainer>

									<CurrentMonthYear>{getMonthAndYearFormat(params.date)}</CurrentMonthYear>

									<ChangeMonthContainer
										onClick={() => {
											if (!params.nextMonthButtonDisabled) params.increaseMonth();
										}}
										allowClick={!params.nextMonthButtonDisabled}
									>
										<RightAngleIcon />
									</ChangeMonthContainer>
								</HeaderMonthYearSelected>
							</>
						)}
					/>
				</ShowDatePickerContainer>
			)}

			{showCalendar.to && (
				<ShowDatePickerContainer y="top" x="left">
					<ReactDatePicker
						inline
						selected={
							state.endExpirationDate ? transformDateToPicker(state.endExpirationDate) : new Date()
						}
						minDate={
							state.initExpirationDate
								? transformDateToPicker(state.initExpirationDate)
								: new Date()
						}
						locale={'es'}
						onChange={(date) => onChangeDate(date as Date, 'to')}
						renderCustomHeader={(params) => (
							<>
								<HeaderMonthYearSelected>
									<ChangeMonthContainer
										onClick={() => {
											if (!params.prevMonthButtonDisabled) params.decreaseMonth();
										}}
										allowClick={!params.prevMonthButtonDisabled}
									>
										<LeftAngleIcon />
									</ChangeMonthContainer>

									<CurrentMonthYear>{getMonthAndYearFormat(params.date)}</CurrentMonthYear>

									<ChangeMonthContainer
										onClick={() => {
											if (!params.nextMonthButtonDisabled) params.increaseMonth();
										}}
										allowClick={!params.nextMonthButtonDisabled}
									>
										<RightAngleIcon />
									</ChangeMonthContainer>
								</HeaderMonthYearSelected>
							</>
						)}
					/>
				</ShowDatePickerContainer>
			)}

			{state.expirationType === 'num_payments' && (
				<Input
					label="Número de pagos exitosos"
					name="numPayments"
					value={state.numPayments}
					onChange={handleChangeInput}
					variant="large"
					placeholder="1"
					min={1}
					labelFocus
					hasFocus
					fontSize="16px"
				/>
			)}

			{selectedToEdit && selectedToEdit.expirationtype === 'NUM_PAYMENTS' && (
				<Input
					label="Número de pagos exitosos"
					name="numPayments"
					value={selectedToEdit.numpayments}
					disabled
					variant="large"
					placeholder="1"
					min={1}
					labelFocus
					hasFocus
					fontSize="16px"
				/>
			)}
		</>
	);
};

export { TypeExpiration };
